import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AnalyticsConsent, openCookiePreferences } from './AnalyticsConsent';

interface PublicLayoutProps {
  children: React.ReactNode;
}

interface CategoryItem {
  label: string;
  to: string;
}

interface NavCategory {
  label: string;
  items: CategoryItem[];
}

const PAGE_METADATA: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'PerfectOddsNL | Surebetmeldingen en tracker',
    description: 'Ontvang live en pre-match surebetmeldingen via Telegram en houd je resultaten bij in de Surebet Tracker.',
  },
  '/pricing': {
    title: 'Abonnementen | PerfectOddsNL',
    description: 'Vergelijk de gratis groep en de Pro-, Pre-match- en Live-abonnementen van PerfectOddsNL.',
  },
  '/surebets': {
    title: 'Wat zijn surebets? | PerfectOddsNL',
    description: 'Leer hoe surebets, oddsverschillen, inzetverdelingen en theoretische uitbetalingen wiskundig werken.',
  },
  '/tracker-uitleg': {
    title: 'Surebet Tracker | PerfectOddsNL',
    description: 'Bekijk hoe je geplaatste surebets, winst, ROI en kosten overzichtelijk bijhoudt.',
  },
  '/tracker/surebet-history': {
    title: 'Surebet archief | PerfectOddsNL',
    description: 'Doorzoek het openbare PerfectOddsNL-archief met historische surebetkansen en resultaten.',
  },
  '/calculator': {
    title: 'Surebet calculator | PerfectOddsNL',
    description: 'Bereken de inzetverdeling, uitbetaling, winst en ROI van twee of drie odds.',
  },
  '/bookmakers': {
    title: 'Bookmakers | PerfectOddsNL',
    description: 'Bekijk de bookmakers en platformen die relevant zijn voor Nederlandse surebetters.',
  },
  '/markten': {
    title: 'Surebetmarkten | PerfectOddsNL',
    description: 'Ontdek op welke sportmarkten en uitkomsten surebetkansen kunnen ontstaan.',
  },
  '/veelgestelde-vragen': {
    title: 'Veelgestelde vragen | PerfectOddsNL',
    description: 'Antwoorden op veelgestelde vragen over surebetten, abonnementen, Telegram en de tracker.',
  },
  '/contact': {
    title: 'Contact | PerfectOddsNL',
    description: 'Neem contact op met PerfectOddsNL over je account, abonnement, Telegram of de Surebet Tracker.',
  },
  '/checkout/success': {
    title: 'Betaling geslaagd | PerfectOddsNL',
    description: 'Je PerfectOddsNL-betaling is ontvangen. Controleer hier je abonnement en vervolgstappen.',
  },
  '/telegram': {
    title: 'Telegram | PerfectOddsNL',
    description: 'Lees hoe je PerfectOddsNL-surebetmeldingen en toegang via Telegram gebruikt.',
  },
  '/kennisbank/risicos-surebetten': {
    title: "Risico's van surebetting | PerfectOddsNL",
    description: 'Lees welke risico’s bij surebetting horen en hoe je odds, markten en inzetten zorgvuldig controleert.',
  },
  '/login': {
    title: 'Inloggen | PerfectOddsNL',
    description: 'Log in op je PerfectOddsNL-account.',
  },
  '/wachtwoord-vergeten': {
    title: 'Wachtwoord vergeten | PerfectOddsNL',
    description: 'Vraag veilig een link aan om het wachtwoord van je PerfectOddsNL-account opnieuw in te stellen.',
  },
  '/wachtwoord-resetten': {
    title: 'Nieuw wachtwoord instellen | PerfectOddsNL',
    description: 'Stel een nieuw wachtwoord in voor je PerfectOddsNL-account.',
  },
  '/register': {
    title: 'Registreren | PerfectOddsNL',
    description: 'Maak een PerfectOddsNL-account aan en kies daarna het abonnement dat bij je past.',
  },
  '/registreren/afronden': {
    title: 'Google-registratie afronden | PerfectOddsNL',
    description: 'Bevestig de voorwaarden en rond je PerfectOddsNL-registratie veilig af.',
  },
  '/mijn-account': {
    title: 'Mijn account | PerfectOddsNL',
    description: 'Beheer je PerfectOddsNL-profiel, abonnement en Telegramtoegang.',
  },
  '/tracker': {
    title: 'Mijn tracker | PerfectOddsNL',
    description: 'Beheer je geplaatste surebets, resultaten, ROI en kosten.',
  },
  '/geen-toegang': {
    title: 'Geen toegang | PerfectOddsNL',
    description: 'Bekijk wat je nodig hebt om toegang te krijgen tot dit onderdeel van PerfectOddsNL.',
  },
  '/admin': {
    title: 'Beheer | PerfectOddsNL',
    description: 'Beheer PerfectOddsNL-gebruikers, abonnementen en platforminstellingen.',
  },
  '/algemene-voorwaarden': {
    title: 'Algemene voorwaarden | PerfectOddsNL',
    description: 'Lees de algemene voorwaarden van PerfectOddsNL.',
  },
  '/privacybeleid': {
    title: 'Privacybeleid | PerfectOddsNL',
    description: 'Lees hoe PerfectOddsNL met persoonsgegevens en accountgegevens omgaat.',
  },
};

export const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { logout, isLoggedIn, isPaidUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const storedTheme = localStorage.getItem('public-theme');
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('public-theme', theme);
  }, [theme]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const loggedIn = isLoggedIn();
  const paidUser = isPaidUser();
  const adminUser = isAdmin();

  useEffect(() => {
    const metadata = PAGE_METADATA[location.pathname]
      || (location.pathname.startsWith('/checkout')
        ? {
            title: 'Checkout | PerfectOddsNL',
            description: 'Controleer je gekozen PerfectOddsNL-abonnement en ga veilig verder naar de betaling.',
          }
        : {
            title: 'PerfectOddsNL',
            description: 'Live en pre-match surebetmeldingen, een persoonlijke tracker en praktische surebettools.',
          });
    document.title = metadata.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute('content', metadata.description);
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    canonical?.setAttribute('href', `https://perfectoddsnl.com${location.pathname === '/' ? '/' : location.pathname}`);
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setExpandedCategory(null);
    setHoveredCategory(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setExpandedCategory(null);
        setHoveredCategory(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const navCategories: Record<string, NavCategory> = useMemo(() => ({
    surebets: {
      label: 'Surebet gids',
      items: [
        { label: 'Wat zijn surebets?', to: '/surebets' },
        { label: 'Risico\'s van surebetting', to: '/kennisbank/risicos-surebetten' },
      ],
    },
    tracker: {
      label: 'Tracker',
      items: paidUser || adminUser
        ? [
            { label: 'Mijn tracker', to: '/tracker' },
            { label: 'Surebet archief', to: '/tracker/surebet-history' },
            { label: 'Surebet calculator', to: '/calculator' },
          ]
        : [
            { label: 'Surebet Tracker', to: '/tracker-uitleg' },
            { label: 'Surebet archief', to: '/tracker/surebet-history' },
            { label: 'Surebet calculator', to: '/calculator' },
          ],
    },
    bookmakers: {
      label: 'Bookmakers',
      items: [
        { label: 'Bookmakers', to: '/bookmakers' },
        { label: 'Markten', to: '/markten' },
      ],
    },
    support: {
      label: 'Support',
      items: [
        { label: 'Telegram koppelen', to: '/telegram' },
        { label: 'Contact', to: '/contact' },
        { label: 'FAQ', to: '/veelgestelde-vragen' },
      ],
    },
  }), [adminUser, paidUser]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleCategoryMouseEnter = (categoryKey: string) => {
    if (!isTouchDevice) {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      setHoveredCategory(categoryKey);
    }
  };

  const handleCategoryMouseLeave = () => {
    if (!isTouchDevice) {
      closeTimeoutRef.current = setTimeout(() => {
        setHoveredCategory(null);
      }, 150);
    }
  };

  const handleDropdownMouseEnter = (categoryKey: string) => {
    if (!isTouchDevice) {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      setHoveredCategory(categoryKey);
    }
  };

  const handleDropdownMouseLeave = () => {
    if (!isTouchDevice) {
      closeTimeoutRef.current = setTimeout(() => {
        setHoveredCategory(null);
      }, 150);
    }
  };

  const handleMobileCategoryToggle = (categoryKey: string) => {
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
  };

  return (
    <div className="public-shell" data-theme={theme}>
      <a href="#main-content" className="skip-link">Naar hoofdinhoud</a>
      <header className="public-navbar">
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" aria-label="PerfectOddsNL home">
            <img src="/assets/dark-logo-POnl.png" alt="PerfectOddsNL" className="logo-image logo-dark-bg" />
            <img src="/assets/light-logo-POnl.png" alt="PerfectOddsNL" className="logo-image logo-light-bg" />
            <span className="logo-text">PerfectOddsNL</span>
          </Link>

          {/* Desktop navigation */}
          <nav className="navbar-nav desktop-nav">
            <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} end>
              Home
            </NavLink>

            <NavLink to="/pricing" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              Abonnementen
            </NavLink>

            {Object.entries(navCategories).map(([key, category]) => (
              <div
                key={key}
                className="nav-item-wrapper"
                onMouseEnter={() => handleCategoryMouseEnter(key)}
                onMouseLeave={handleCategoryMouseLeave}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setHoveredCategory(null);
                  }
                }}
              >
                <button
                  type="button"
                  className={`nav-item dropdown-trigger ${hoveredCategory === key ? 'active' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={hoveredCategory === key}
                  onFocus={() => setHoveredCategory(key)}
                  onClick={() => setHoveredCategory((current) => (current === key ? null : key))}
                >
                  {category.label}
                </button>

                {hoveredCategory === key && (
                  <div
                    className="dropdown-menu"
                    role="menu"
                    onMouseEnter={() => handleDropdownMouseEnter(key)}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    {category.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="navbar-actions">
            <button
              type="button"
              className="action-button theme-toggle"
              onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
              aria-label={theme === 'dark' ? 'Schakel light mode in' : 'Schakel dark mode in'}
              aria-pressed={theme === 'dark'}
            >
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            {loggedIn ? (
              <>
                <Link to="/mijn-account" className="action-button login-button">
                  Mijn account
                </Link>
                {adminUser && (
                  <Link to="/admin" className="action-button login-button admin-header-button">
                    Admin
                  </Link>
                )}
                <button type="button" className="action-button login-button" onClick={handleLogout}>
                  Uitloggen
                </button>
              </>
            ) : (
              <>
                <Link to="/register" className="action-button register-button">
                  Registreren
                </Link>
                <Link to="/login" className="action-button login-button">
                  Inloggen
                </Link>
              </>
            )}

            {/* Mobile menu toggle */}
            <button
              type="button"
              className="action-button mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav" id="mobile-navigation" aria-label="Mobiele navigatie">
            <NavLink
              to="/"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              end
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </NavLink>

            <NavLink
              to="/pricing"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Abonnementen
            </NavLink>

            {Object.entries(navCategories).map(([key, category]) => (
              <div key={key} className="mobile-category">
                <button
                  type="button"
                  className={`mobile-category-toggle ${expandedCategory === key ? 'expanded' : ''}`}
                  onClick={() => handleMobileCategoryToggle(key)}
                  aria-expanded={expandedCategory === key}
                >
                  {category.label}
                  <span className="toggle-icon">▼</span>
                </button>

                {expandedCategory === key && (
                  <div className="mobile-category-items">
                    {category.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => `mobile-category-item ${isActive ? 'active' : ''}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loggedIn && adminUser && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </NavLink>
            )}

            {loggedIn && (
              <>
                <NavLink
                  to="/mijn-account"
                  className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mijn account
                </NavLink>
                <button type="button" className="mobile-nav-item mobile-logout-button" onClick={handleLogout}>
                  Uitloggen
                </button>
              </>
            )}

          </nav>
        )}
      </header>

      <main className="public-main" id="main-content" tabIndex={-1}>{children}</main>

      <footer className="public-site-footer">
        <div className="public-site-footer-inner">
          <div className="footer-brand">
            <Link to="/" className="footer-logo" aria-label="PerfectOddsNL home">
              <img src={theme === 'dark' ? '/assets/light-logo-POnl.png' : '/assets/dark-logo-POnl.png'} alt="" />
              <span>PerfectOddsNL</span>
            </Link>
            <p>Surebetmeldingen via Telegram, met één centrale plek voor je tracker, resultaten en kosten.</p>
          </div>

          <nav className="footer-links" aria-label="Footernavigatie">
            <div>
              <strong>Ontdek</strong>
              <Link to="/pricing">Abonnementen</Link>
              <Link to="/surebets">Wat zijn surebets?</Link>
              <Link to="/tracker-uitleg">Surebet Tracker</Link>
              <Link to="/calculator">Calculator</Link>
            </div>
            <div>
              <strong>Support</strong>
              <Link to="/veelgestelde-vragen">Veelgestelde vragen</Link>
              <Link to="/contact">Contact</Link>
              <a href="mailto:info@perfectoddsnl.com">info@perfectoddsnl.com</a>
            </div>
            <div>
              <strong>Voorwaarden</strong>
              <Link to="/algemene-voorwaarden">Algemene voorwaarden</Link>
              <Link to="/privacybeleid">Privacybeleid</Link>
              <button type="button" className="footer-cookie-button" onClick={openCookiePreferences}>Cookievoorkeuren</button>
              <span>18+ · Speel bewust</span>
            </div>
          </nav>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} PerfectOddsNL</span>
          <span>Geen winstgarantie. Controleer odds en inzetten altijd zelf.</span>
        </div>
      </footer>
      <AnalyticsConsent />
    </div>
  );
};
